import React, {FC} from 'react';
import {toast} from 'react-toastify';
import {PopoverArrow} from '@radix-ui/react-popover';
import {switchProfile, VerdocsEndpoint} from '@verdocs/js-sdk';
import {BuildingOfficeIcon, PlusIcon} from '@heroicons/react/24/outline';
import {CheckIcon, ChevronDownIcon, UserCircleIcon} from '@heroicons/react/24/solid';
import {queryClient, useCurrentProfile, useProfiles} from '../react-sdk/queries';
import {Popover, PopoverTrigger, PopoverContent} from './Popover';
import {CONFIG} from '../config';
import {URLS} from '../Routing';

/**
 * The org's square thumbnail, nearly filling a white rounded tile. With no thumbnail the tile is outlined
 * with the building glyph, so orgs without a logo still read as orgs.
 */
const OrgTile: FC<{src?: string | null; size: number; className?: string}> = ({src, size, className = ''}) => (
  <span
    className={`flex flex-shrink-0 items-center justify-center overflow-hidden rounded-md border ${
      src ? 'border-black/10 bg-white' : 'border-[#dcdee5] bg-[#fbfbfc] text-gray-500'
    } ${className}`}
    style={{width: size, height: size}}
  >
    {src ? <img src={src} alt="" className="h-full w-full object-contain p-[3px]" /> : <BuildingOfficeIcon style={{width: size * 0.6, height: size * 0.6}} />}
  </span>
);

const PersonTile: FC<{src?: string | null; size: number}> = ({src, size}) =>
  src ? (
    <img src={src} alt="" className="flex-shrink-0 rounded-md object-cover" style={{width: size, height: size}} />
  ) : (
    <UserCircleIcon className="flex-shrink-0 text-gray-400" style={{width: size, height: size}} />
  );

const ROW = 'flex h-10 w-full items-center gap-2.5 rounded-md px-1.5 -mx-1.5 text-[14px] font-medium no-underline transition-colors';

export const ProfileChooser: FC = () => {
  const profilesQuery = useProfiles();
  const currentProfile = useCurrentProfile();

  // Personal account first, then organizations by name. The api returns them in creation order.
  const profiles = [...(profilesQuery.data || [])].sort((a, b) => {
    const aPersonal = a.organization_id === CONFIG.PERSONAL_ORGID ? 0 : 1;
    const bPersonal = b.organization_id === CONFIG.PERSONAL_ORGID ? 0 : 1;
    return aPersonal - bPersonal || (a.organization?.name || '').localeCompare(b.organization?.name || '');
  });
  const isPersonal = currentProfile?.organization_id === CONFIG.PERSONAL_ORGID;

  const handleSwitchProfile = async (profileId: string) => {
    try {
      console.log('Switching to profile', profileId);
      const r = await switchProfile(VerdocsEndpoint.getDefault(), profileId);
      VerdocsEndpoint.getDefault().setToken(r.access_token);
      queryClient.clear();
      window.location.reload();
    } catch (e: any) {
      console.log('Error switching profiles', e);
      toast.error('Unable to switch profiles: ' + e.message);
    }
  };

  return (
    <Popover>
      <PopoverTrigger className="flex flex-row gap-3 items-center tablet:mt-3 tablet:mb-8 justify-between w-full">
        <div className="flex flex-[0_0_44px] pl-3">
          {isPersonal ? <PersonTile src={currentProfile?.picture} size={32} /> : <OrgTile src={currentProfile?.organization?.thumbnail_url} size={32} />}
        </div>

        <div className="flex flex-1 flex-col">
          <div className="overflow-hidden whitespace-nowrap text-ellipsis text-sm text-left">
            {currentProfile?.first_name} {currentProfile?.last_name}
          </div>
          <div className="opacity-70 overflow-hidden whitespace-nowrap text-ellipsis text-sm text-left">
            {isPersonal ? 'Personal Account' : currentProfile?.organization?.name}
          </div>
        </div>

        <div className="flex flex-0 items-center pr-3">
          <ChevronDownIcon width={20} height={20} />
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="ml-2 max-h-[410px] min-w-[240px] overflow-y-auto overflow-x-hidden rounded-lg border border-[#e3e5ec] bg-white p-3 text-[#33364b] shadow-[0_8px_24px_rgba(20,20,40,0.12)]"
        sideOffset={5}
      >
        <PopoverArrow className="fill-white" />

        <div className="mb-1 px-1 text-[12px] font-medium text-[#9b9ea8]">Switch profile</div>

        {profiles.map((profile) => {
          const personal = profile.organization_id === CONFIG.PERSONAL_ORGID;
          return (
            <a
              key={profile.id}
              onClick={() => handleSwitchProfile(profile.id)}
              className={`${ROW} cursor-pointer ${profile.current ? 'bg-[#efedfb] text-[#654dcb]' : 'text-[#33364b] hover:bg-[#f5f5fa]'}`}
            >
              {personal ? <PersonTile src={profile.picture} size={28} /> : <OrgTile src={profile.organization?.thumbnail_url} size={28} />}
              <span className="min-w-0 flex-1">
                {personal ? (
                  <>
                    <span className="block truncate leading-4">
                      {profile.first_name} {profile.last_name}
                    </span>
                    <span className="block text-[11px] italic leading-4 opacity-70">Personal</span>
                  </>
                ) : (
                  <span className="block truncate">{profile.organization?.name}</span>
                )}
              </span>
              {profile.current && <CheckIcon className="h-5 w-5 flex-shrink-0" />}
            </a>
          );
        })}

        <div className="my-1.5 h-px bg-[#eceef3]" />

        <a href={URLS.CREATE_ORG.build()} className={`${ROW} text-[#33364b] hover:bg-[#f5f5fa]`}>
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border border-[#dcdee5] bg-[#fbfbfc] text-gray-500">
            <PlusIcon className="h-4 w-4" />
          </span>
          <span>New organization</span>
        </a>
      </PopoverContent>
    </Popover>
  );
};
